import { CHANNEL_STATUS, TChannelStatus } from '@/entities/channel';

const EXPIRING_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

export const CHANNEL_LIST_STATUS = {
  ACTIVE: 'active',
  DISABLED: 'disabled',
  AWAITING_PAYMENT: 'awaiting_payment',
  SYNCING: 'syncing',
  AUTH_REQUIRED: 'auth_required',
  SUSPENDED: 'suspended',
  SUBSCRIPTION_EXPIRING: 'subscription_expiring',
  ENDED: 'ended',
} as const;

export type TChannelListStatus =
  (typeof CHANNEL_LIST_STATUS)[keyof typeof CHANNEL_LIST_STATUS];

export type TChannelPrimaryAction = 'authorize' | 'renew' | 'pay' | null;

type ChannelListStatusInput = {
  status: TChannelStatus;
  isActive?: boolean;
  subscriptionEndDate?: string | null;
  subscriptionUnavailable?: boolean;
  now?: Date;
};

const isConnected = (status: TChannelStatus) => status === CHANNEL_STATUS.READY;

const isSyncing = (status: TChannelStatus) =>
  status === CHANNEL_STATUS.AUTHORIZED ||
  status === CHANNEL_STATUS.CONTACTS_SYNCED ||
  status === CHANNEL_STATUS.CHATS_SYNCED;

const getMsUntilEnd = (endDate?: string | null, now = new Date()) => {
  if (!endDate) return null;

  const time = new Date(endDate).getTime();
  if (Number.isNaN(time)) return null;

  return time - now.getTime();
};

export const getChannelListStatus = ({
  status,
  isActive,
  subscriptionEndDate,
  subscriptionUnavailable,
  now,
}: ChannelListStatusInput): TChannelListStatus => {
  const msUntilEnd = getMsUntilEnd(subscriptionEndDate, now);

  if (status === CHANNEL_STATUS.PENDING_PAYMENT) {
    return CHANNEL_LIST_STATUS.AWAITING_PAYMENT;
  }

  if (status === CHANNEL_STATUS.SUSPENDED) {
    return CHANNEL_LIST_STATUS.SUSPENDED;
  }

  if (subscriptionUnavailable || (msUntilEnd !== null && msUntilEnd <= 0)) {
    return CHANNEL_LIST_STATUS.ENDED;
  }

  if (isConnected(status) && isActive === false) {
    return CHANNEL_LIST_STATUS.DISABLED;
  }

  if (isSyncing(status)) {
    return CHANNEL_LIST_STATUS.SYNCING;
  }

  if (
    msUntilEnd !== null &&
    msUntilEnd > 0 &&
    msUntilEnd <= EXPIRING_THRESHOLD_MS
  ) {
    return CHANNEL_LIST_STATUS.SUBSCRIPTION_EXPIRING;
  }

  if (isConnected(status) && isActive !== false) {
    return CHANNEL_LIST_STATUS.ACTIVE;
  }

  return CHANNEL_LIST_STATUS.AUTH_REQUIRED;
};

export const getChannelPrimaryAction = (
  input: ChannelListStatusInput
): TChannelPrimaryAction => {
  const listStatus = getChannelListStatus(input);

  if (listStatus === CHANNEL_LIST_STATUS.ENDED) {
    return 'pay';
  }

  if (listStatus === CHANNEL_LIST_STATUS.SUBSCRIPTION_EXPIRING) {
    return isConnected(input.status) && input.isActive !== false
      ? 'renew'
      : 'authorize';
  }

  if (listStatus === CHANNEL_LIST_STATUS.AUTH_REQUIRED) {
    return 'authorize';
  }

  return null;
};
